import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import StartButton from './StartButton';
import { useButtonSpotlight, useGoldShine, useReveal } from '@/hooks/useSiteEffects';
import { cn } from '@/lib/utils';

/** Scroll to the top on navigation, or to the target section for hash links. */
const useScrollOnNavigate = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait a frame so the destination page has rendered its sections.
      const id = decodeURIComponent(hash.slice(1));
      requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
};

/**
 * Mobile-only persistent "Start for free" bar. It appears once the visitor
 * scrolls past the first screen and is hidden inside the intake flow, where
 * it would compete with the form's own buttons.
 */
const MobileStartBar = ({ visible }: { visible: boolean }) => (
  <div
    className={cn(
      'fixed inset-x-0 bottom-0 z-40 border-t border-rule bg-knightblack/95 px-4 py-3 backdrop-blur-md md:hidden',
      'motion-safe:transition-transform motion-safe:duration-300',
      visible ? 'translate-y-0' : 'pointer-events-none translate-y-full',
    )}
    aria-hidden={!visible}
  >
    <StartButton className="w-full" />
  </div>
);

const SiteLayout = () => {
  useScrollOnNavigate();
  const { pathname } = useLocation();
  useGoldShine(pathname);
  useReveal(pathname);
  useButtonSpotlight();
  const inIntake = pathname.startsWith('/start');
  const [pastFold, setPastFold] = useState(false);

  useEffect(() => {
    const onScroll = () => setPastFold(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    // Bottom padding on mobile keeps the sticky CTA from covering the footer.
    <div className={cn('flex min-h-screen flex-col overflow-x-hidden', !inIntake && 'pb-20 md:pb-0')}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-goldcore focus:px-4 focus:py-2 focus:text-knightblack"
      >
        Skip to content
      </a>
      <Navbar />
      <main id="main" tabIndex={-1} className="flex-1 pt-[68px] focus:outline-none">
        <Outlet />
      </main>
      <Footer />
      {!inIntake && <MobileStartBar visible={pastFold} />}
    </div>
  );
};

export default SiteLayout;
